import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import application from 'application';

import { buildDialogContentBoxAttributes } from './utilities';

import './index.scss';

function SimpleDialog(props) {

  const stackTopClass = props.stackTopClass;

  const contentContainerReference = useRef(null);
  const simpleDialogMainReference = useRef(null);
  const simpleDialogContentReference = useRef(null);
  const centerAlignmentContainerReference = useRef(null);

  const pointerDownStartedInsideContentRef = useRef(false);

  const [rootState] = useState(function buildInitialState() {
    return {
      activeDialogsCount: application.dialogsManager.getActiveDialogsCount()
    };
  });

  useEffect(function onLoad() {
    application.dialogsManager.incrementActiveDialogsCount();
    tryAddStackTopClass();

    return function onUninitialize() {
      tryRemoveStackTopClass();
      application.dialogsManager.decrementActiveDialogsCount();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function registerEvents() {
    const unlistenDocumentKeyDownEvent = listenToDocumentKeyDownEvent();

    return function unregisterEventHandlers() {
      unlistenDocumentKeyDownEvent();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function registerWindowPointerUpReset() {
    function handleWindowPointerUp() {
      pointerDownStartedInsideContentRef.current = false;
    }

    window.addEventListener('pointerup', handleWindowPointerUp, { passive: true });

    return function unregisterWindowPointerUpReset() {
      window.removeEventListener('pointerup', handleWindowPointerUp);
    };
  }, []);

  function listenToDocumentKeyDownEvent() {
    function handleDocumentKeyDownEvent(event) {
      tryHideDialog(event);
    }

    document.addEventListener('keydown', handleDocumentKeyDownEvent);

    return function unlistenDocumentKeyDownEvent() {
      document.removeEventListener('keydown', handleDocumentKeyDownEvent);
    };
  }

  function tryHideDialog(event) {
    if (event.key !== 'Escape') {
      return;
    }

    if (props.cancelOnEscape !== true) {
      return;
    }

    if (typeof props.onClose === 'function') {
      props.onClose();
    }
  }

  function tryAddStackTopClass() {
    let classNames = ['dialog-is-on'];

    if (typeof stackTopClass === 'string') {
      classNames.push(stackTopClass.trim());
    }

    document.body.classList.add(...classNames);
  }

  function tryRemoveStackTopClass() {
    let classNames = [];

    if (application.dialogsManager.getActiveDialogsCount() === 1) {
      classNames.push('dialog-is-on');
    }

    if (typeof stackTopClass === 'string') {
      classNames.push(stackTopClass.trim());
    }

    document.body.classList.remove(...classNames);
  }

  function handleDialogContentPointerDownCapture(event) {
    if (props.promptMode === true) {
      return;
    }

    pointerDownStartedInsideContentRef.current = contentContainerReference.current.contains(event.target);
  }

  function tryCallOnCloseHandler(event) {
    if (props.promptMode === true) {
      return;
    }

    if (typeof event.button === 'number' && event.button !== 0) {
      return;
    }

    if (pointerDownStartedInsideContentRef.current === true) {
      pointerDownStartedInsideContentRef.current = false;
      return;
    }

    if (
      event.target !== simpleDialogContentReference.current &&
      event.target !== centerAlignmentContainerReference.current
    ) {
      return;
    }

    event.stopPropagation();

    if (typeof props.onClose === 'function') {
      props.onClose();
    }
  }

  function renderContentHolder() {
    let contentHolderClassName = 'simple-dialog-dialog-content-holder';

    if (props.contentHolderClassName) {
      contentHolderClassName += ' ' + props.contentHolderClassName;
    }

    const contentHolderAttributes = {
      className: contentHolderClassName,
      ref: contentContainerReference
    };

    return <div {...contentHolderAttributes}>{props.children}</div>;
  }

  function renderMain() {
    const activeDialogsCount = rootState.activeDialogsCount;

    const mainAttributes = {
      className: `simple-dialog-main dialog-${activeDialogsCount}`,
      ref: simpleDialogMainReference
    };

    const simpleDialogMainProperties = {
      ...buildDialogContentBoxAttributes(props),
      className: 'simple-dialog-content hide-scrollbar',
      onPointerDownCapture(event) {
        handleDialogContentPointerDownCapture(event);
      },
      onPointerUp(event) {
        tryCallOnCloseHandler(event);
      },
      ref: simpleDialogContentReference
    };

    const centerAlignmentContainerProperties = {
      className: 'simple-dialog-content-center-alignment-container',
      ref: centerAlignmentContainerReference
    };

    if (props.transparentBackground === true) {
      mainAttributes.className += ' transparent-background';
    }

    return (
      <div {...mainAttributes}>
        <div className="overflow-wrapper hide-scrollbar">
          <div {...simpleDialogMainProperties}>
            <div {...centerAlignmentContainerProperties}>{renderContentHolder()}</div>
          </div>
        </div>
      </div>
    );
  }

  return ReactDOM.createPortal(renderMain(), document.getElementById('root-modal-container'));
}

export default SimpleDialog;